// @ts-check
import { Component } from '@theme/component';

/**
 * The promotional fly-out: a tab pinned to the bottom of every page that opens a row of promo
 * cards over a dimmed storefront.
 *
 * Open/closed is remembered for the session, not forever: the fly-out is on every page, so a
 * shopper who closes it should not have to close it again on the next click, and a new visit
 * still gets the offer. Closing is what gets remembered - the auto-open delay decides the first
 * page only, and any click on the tab cancels a pending auto-open.
 *
 * The panel stays in the DOM while closed - parked below the floor by one transform on the sheet
 * - and carries `inert` so its links stay out of the tab order until it is open.
 */

const STORAGE_KEY = 'custom:promo-flyout';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} tab - The bottom tab, and the panel's disclosure button.
 * @property {HTMLElement} panel - The card row.
 * @property {HTMLButtonElement} scrim - The dimmed page behind it; a click closes.
 *
 * @extends {Component<Refs>}
 */
class CustomPromoFlyout extends Component {
  requiredRefs = ['tab', 'panel', 'scrim'];

  #controller = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #autoOpenTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const remembered = readState();
    this.#setOpen(remembered === true, { remember: false });

    // The load state is applied before this, so the first frame paints it without a slide.
    requestAnimationFrame(() => this.setAttribute('data-ready', ''));

    // Auto-open is for a shopper who has not answered the fly-out yet; once they close it, the
    // session's answer stands.
    if (remembered === null) this.#scheduleAutoOpen();

    document.addEventListener('keydown', this.#onKeydown, { signal });

    // Theme editor: show what is selected. Card events bubble here; the section's fires on the
    // wrapper above this element, so it is caught on the document and matched by containment.
    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
      document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#autoOpenTimer);
    this.#controller.abort();
  }

  /** Flips the panel. Bound declaratively from the tab. */
  toggle = () => {
    this.#cancelAutoOpen();
    this.#setOpen(!this.hasAttribute('data-open'));
  };

  /** Closes the panel. Bound declaratively from the scrim. */
  close = () => {
    this.#cancelAutoOpen();
    if (!this.hasAttribute('data-open')) return;
    // An auto-opened panel never had focus; pulling it to the tab would yank the shopper's place.
    const hadFocus = this.contains(document.activeElement);
    this.#setOpen(false);
    if (hadFocus) this.refs.tab.focus();
  };

  #scheduleAutoOpen() {
    const delay = Number(this.dataset.autoOpenDelay);
    if (!Number.isFinite(delay)) return;

    this.#autoOpenTimer = setTimeout(() => {
      // Not remembered: the shopper did not ask for this, so their next close is still the
      // session's first answer.
      this.#setOpen(true, { remember: false });
    }, delay);
  }

  #cancelAutoOpen() {
    clearTimeout(this.#autoOpenTimer);
    this.#autoOpenTimer = undefined;
  }

  #onEditorSelect = () => {
    this.#cancelAutoOpen();
    this.#setOpen(true, { remember: false });
  };

  /** @param {Event} event - The editor's section:select, which fires on the section wrapper. */
  #onSectionSelect = (event) => {
    const target = event.target;
    if (target instanceof Node && target.contains(this)) this.#onEditorSelect();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    // An open modal (cart drawer, menu) owns this Escape.
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      document.querySelector('dialog:modal')
    ) {
      return;
    }
    this.close();
  };

  /**
   * @param {boolean} open - Whether the panel should be showing.
   * @param {{ remember?: boolean }} [options] - `remember: false` for a state the shopper did
   *   not choose.
   */
  #setOpen(open, options) {
    this.toggleAttribute('data-open', open);
    this.refs.tab.setAttribute('aria-expanded', String(open));
    this.refs.panel.toggleAttribute('inert', !open);

    if (options?.remember === false) return;
    writeState(open);
  }
}

/**
 * @returns {boolean | null} The remembered state, or null when this session has none - or when
 *   storage is unavailable, which a private window and a blocked-cookies setting both produce.
 */
function readState() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === null) return null;
    return stored === 'open';
  } catch {
    return null;
  }
}

/** @param {boolean} open - The state to carry to the next page. */
function writeState(open) {
  try {
    sessionStorage.setItem(STORAGE_KEY, open ? 'open' : 'closed');
  } catch {
    // Storage is a convenience here; the fly-out works without it.
  }
}

if (!customElements.get('custom-promo-flyout')) {
  customElements.define('custom-promo-flyout', CustomPromoFlyout);
}
